import { menu, type MenuNode } from './menu';

/**
 * The URL, translated to and from the key the shell navigates by.
 *
 * The shell has no router — navigation is a key swap in `pages/registry.tsx` — and for a while it
 * had no URL either: the key was component state that started at `branch` on every load, so a
 * reload dropped whoever was on `/bookings` back onto the branches screen, and a bookmark or a
 * shared link pointed at nothing in particular. This is the missing half. `menu.ts` already carries
 * a `path` on every leaf, and the sidebar already renders it as a real `href`; all that was absent
 * was reading it back.
 *
 * Deliberately pure and free of `window`, for two reasons: the manifest can then be checked for
 * duplicate paths by a test running under the suite's default `node` environment, and the one place
 * that touches `history` stays in `App.tsx` where the state it drives lives.
 *
 * The manifest stays the single source of truth — a screen that changes its path changes it in one
 * place, and nothing here needs to know that it did.
 */

/**
 * The key the shell opens on, and what `/` means.
 *
 * It was `branch` — a Setup master — under a comment saying there was no dashboard for it to mean
 * instead. There is one now, and it is the right answer for a second reason: `today` carries no
 * `requires`, so unlike the branches list it cannot refuse the person who lands on it.
 * `shell/landing.ts` still overrides this per person, and now rarely has to.
 */
export const HOME_KEY = 'today';

/**
 * What a path the manifest has never heard of resolves to.
 *
 * A deliberate non-key: `isBuilt` rejects it and `findMenuDestination` finds nothing, so `screenFor`
 * falls through to its 404 exactly as it does for any other unknown key. Answering `HOME_KEY`
 * instead would quietly rewrite a mistyped URL into the branches screen and tell the user their
 * link was fine.
 */
export const NOT_FOUND_KEY = '__not-found__';

const pathByKey = new Map<string, string>();
const keyByPath = new Map<string, string>();

function index(nodes: MenuNode[]): void {
  for (const node of nodes) {
    // Groups carry no path — they are headings, not destinations.
    if (node.path) {
      pathByKey.set(node.key, node.path);
      keyByPath.set(node.path.toLowerCase(), node.key);
    }

    if (node.children) {
      index(node.children);
    }
  }
}

index(menu);

/**
 * Strips everything that is not the path, and the trailing slash with it.
 *
 * `/units/` and `/units` are one screen; a browser produces both — the second from a typed URL that
 * got a slash added, the first from `pushState`. Case is normalised because a hand-typed `/Units`
 * is a typo the manifest can still answer, not a different destination.
 */
function normalize(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0] ?? '';
  const trimmed = path.replace(/\/+$/, '');

  return (trimmed === '' ? '/' : trimmed).toLowerCase();
}

/** The path a key is reached at, or null for a group heading and for {@link NOT_FOUND_KEY}. */
export function pathForKey(key: string): string | null {
  return pathByKey.get(key) ?? null;
}

/** The key a path names — {@link HOME_KEY} at the root, {@link NOT_FOUND_KEY} for anything else. */
export function keyForPath(pathname: string): string {
  const path = normalize(pathname);

  if (path === '/') {
    return HOME_KEY;
  }

  return keyByPath.get(path) ?? NOT_FOUND_KEY;
}
